<script lang="ts">
	import { onMount } from 'svelte';
	import Hydrator from '@reperio-news/core.components/Hydrator.svelte';
	import IntersectionObserver from '@reperio-news/core.components/IntersectionObserver.svelte';
	import Modal from '@reperio-news/adh-www-common/src/shared/blocks/FinanceStrip/Modal.svelte';
	import { DEFAULT_CURRENCIES, fetchFinance, resolveNewsTags, sampleFinance, trackFinance } from '@reperio-news/adh-www-common/src/shared/blocks/FinanceStrip/finance.js';
	import type { z } from 'zod';
	import Data from './Data.js';
	import Config from '../../index.js';
	import { manualIndices, tickerItems } from './items.js';

	export let data: z.infer<typeof Data> = {};
	export let id: string;
	export let globalId: string = null;
	export let globalSource: number = null;
	id; globalId; globalSource;
	$: if (!data) data = {};
	$: pinned = [...new Set(Array.isArray(data.currencies) ? data.currencies.filter((code) => typeof code === 'string') : DEFAULT_CURRENCIES)];
	$: showFuel = data.showFuel !== false;
	$: indices = manualIndices(data.rates);
	$: news = resolveNewsTags(data.news);

	let finance = null;
	let loaded = false;
	let element;
	let intersecting = false;
	let impressionSent = false;
	let modalOpen = false;
	let modalTab = 'currency';
	$: if (__ADMIN__) { finance = sampleFinance(pinned); loaded = true; }
	$: items = tickerItems(finance, pinned, showFuel, indices);
	$: if (!__ADMIN__ && loaded && items.length && intersecting && !impressionSent) {
		impressionSent = true;
		trackFinance('impression');
	}

	onMount(() => {
		if (__ADMIN__) return;
		let active = true;
		fetchFinance().then((result) => {
			if (!active) return;
			finance = result;
			loaded = true;
		});
		return () => { active = false; };
	});

	function open(item) {
		if (__ADMIN__) return;
		modalTab = item.group;
		modalOpen = true;
		trackFinance('open', item.group, item.key);
	}
	function close() {
		modalOpen = false;
		trackFinance('close');
	}
</script>

<Hydrator props={$$props} component="FinanceStrip" hydrate={!__ADMIN__}>
	{#if !loaded || items.length || __ADMIN__}
		<section class="af-finance" aria-label="Curs valutar, carburanți și indici" bind:this={element}>
			{#if !__ADMIN__}<IntersectionObserver {element} bind:intersecting once={true} threshold={0.3} />{/if}
			{#if __ADMIN__}<small class="preview">Previzualizare · date demonstrative</small>{/if}
			<div class="ticker">
				{#if data.title}
					{#if data.url}
						<a class="title" href={data.url} target={data.openInNewTab ? '_blank' : undefined} rel="noopener" on:click={() => trackFinance('title_click', null, data.url)}>{data.title}</a>
					{:else}<span class="title">{data.title}</span>{/if}
				{/if}
				{#if loaded}
					{#each items as item (item.key)}
						<button type="button" class="quote" title={item.title} aria-haspopup="dialog" on:click={() => open(item)}>
							<span class="label">{item.quantity ? `${item.quantity} ` : ''}{item.label}</span>
							<span class="value">{item.value} <span class="unit">{item.unit}</span></span>
							{#if item.change}
								<span class="change" class:up={item.change.direction > 0} class:down={item.change.direction < 0}>
									<span aria-hidden="true">{item.change.direction > 0 ? '▲' : item.change.direction < 0 ? '▼' : '='}</span>
									<span class="sr-only">{item.change.direction > 0 ? 'Creștere' : item.change.direction < 0 ? 'Scădere' : 'Fără variație'}</span>
									{item.change.text}{item.group === 'index' ? ' pp' : ''}
								</span>
							{/if}
						</button>
					{/each}
					{#if __ADMIN__ && !items.length}<span class="empty">Selectați valute, carburanți sau un indice.</span>{/if}
				{:else}
					<div class="loading" aria-hidden="true"><span></span><span></span><span></span><span></span></div>
				{/if}
			</div>
			{#if modalOpen}
				<Modal finance={finance || { bnr: null, fuel: null }} {pinned} {indices} {showFuel} {news} initialTab={modalTab} domain={Config.domain} brandName="Adevărul Financiar" brandLogo={null} on:close={close} />
			{/if}
		</section>
	{/if}
</Hydrator>

<style lang="scss">
	.af-finance {
		--finance-surface: var(--af-surface, #fff);
		--finance-ink: var(--af-ink, #14161a);
		--finance-muted: var(--af-ink-muted, #5c6169);
		--finance-border: var(--af-hairline, #ded5c6);
		--finance-subtle: var(--af-surface-sunk, #efe9de);
		--finance-hover: var(--af-surface-sunk, #efe9de);
		--finance-focus: var(--af-focus, #1d6fe0);
		--finance-accent: var(--af-brand, #0e2a47);
		/* Match the source widget's cost-oriented red increase / green decrease. */
		--finance-increase: var(--af-down, #b3261e);
		--finance-decrease: var(--af-up, #0d724a);
		--finance-font: var(--af-font-sans, Arial, sans-serif);
		width: 100%; min-width: 0; box-sizing: border-box;
		background: var(--af-paper, #f7f3ec); color: var(--finance-ink);
		border-bottom: 1px solid var(--finance-border);
		font: 500 12px / 1.4 var(--finance-font);
	}
	.ticker {
		display: flex; align-items: center; overflow-x: auto; overflow-y: hidden;
		height: 48px; max-width: var(--af-site-max, 62.5rem); margin-inline: auto;
		padding-inline: var(--af-gutter, 1rem); box-sizing: border-box;
		// Keep native touch/trackpad and keyboard scrolling without scrollbar tracks.
		scrollbar-width: none;
		&::-webkit-scrollbar { display: none; width: 0; height: 0; }
	}
	.quote {
		position: relative;
		display: inline-flex; align-items: center; flex: 0 0 auto; gap: 8px;
		min-height: 48px; padding: 0 16px; border: 0; border-right: 1px solid var(--finance-border);
		background: transparent; color: inherit; font: inherit; white-space: nowrap; cursor: pointer;
		&:first-child { padding-left: 0; }
		&:last-child { border-right: 0; }
		&:hover { background: var(--finance-hover); }
		&:focus-visible { outline: 2px solid var(--finance-focus); outline-offset: -3px; }
	}
	.label { font-weight: 700; }
	.value, .change { font-variant-numeric: tabular-nums; }
	.value { font-family: var(--af-font-mono, monospace); }
	.unit, .change { color: var(--finance-muted); font-size: 11px; }
	.change.up { color: var(--finance-increase); }
	.change.down { color: var(--finance-decrease); }
	.title { flex: 0 0 auto; padding-right: 16px; color: inherit; font-weight: 700; }
	.preview { display: block; padding: 4px 16px; color: var(--finance-muted); }
	.empty { padding: 12px; }
	.loading { display: flex; gap: 24px; height: 48px; align-items: center; overflow: hidden; }
	.loading span { width: 160px; height: 12px; flex-shrink: 0; border-radius: 3px; background: var(--finance-subtle); }
	.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
</style>
